/**
 * Guard central de versión — autorefresh seguro sin perder operaciones críticas.
 * Panel admin: modal controlado (UX-SAFE-UPDATE-02).
 * POS/cajero: autorefresh legacy (UX-SAFE-UPDATE-01).
 * @module components/common/AppVersionGuard
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  evaluateAppVersion,
  applyPendingVersionIfSafe,
  applyVersionUpdateNow,
  getVersionCheckIntervalMs,
  registerLegacyServiceWorkerCleanup,
  postponeVersionUpdate,
  readPendingVersion,
  getPostponeDurationMs
} from '../../utils/appVersionGuard';
import { isPosProtectedRoute } from '../../utils/criticalOperationGuard';
import { reconcileSucursalSelection, purgeKnownInvalidSucursalSelection } from '../../utils/sucursalStorageValidation';
import { hasCriticalOperationActive, describeCriticalOperation } from '../../utils/criticalOperationGuard';
import AppVersionUpdateModal from './AppVersionUpdateModal';

const DEFERRED_TOAST_ID = 'app-version-deferred';
const UPDATE_MODAL_TOAST_ID = 'app-version-update-available';

function notifyDeferredUpdate() {
  if (toast.isActive(DEFERRED_TOAST_ID)) return;
  toast.info('La actualización se aplicará cuando termines esta operación.', {
    toastId: DEFERRED_TOAST_ID,
    autoClose: 8000
  });
}

const AppVersionGuard = () => {
  const { pathname } = useLocation();
  const { sucursalesDisponibles } = useAuth() || {};
  const bootstrappedRef = useRef(false);
  const deferredNotifiedRef = useRef(false);
  const posRoute = isPosProtectedRoute(pathname);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [pendingVersion, setPendingVersion] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    purgeKnownInvalidSucursalSelection();
    registerLegacyServiceWorkerCleanup();
  }, []);

  useEffect(() => {
    if (!Array.isArray(sucursalesDisponibles) || sucursalesDisponibles.length === 0) return;
    reconcileSucursalSelection(sucursalesDisponibles);
  }, [sucursalesDisponibles]);

  const handleUpdateAvailable = useCallback((remote) => {
    if (!remote?.commit) return;
    setPendingVersion(remote);
    if (posRoute) return;
    setUpdateModalOpen(true);
  }, [posRoute]);

  const runCheck = useCallback(async () => {
    try {
      const result = await evaluateAppVersion({ pathname });
      if (result.status === 'deferred') {
        if (!deferredNotifiedRef.current) {
          deferredNotifiedRef.current = true;
          notifyDeferredUpdate();
        }
        return;
      }
      if (result.status === 'update_available') {
        handleUpdateAvailable(result.remote);
        return;
      }
      if (result.status === 'applied') {
        window.location.reload();
      }
    } catch (error) {
      console.warn('[AppVersionGuard] check omitido:', error?.message || error);
    }
  }, [pathname, handleUpdateAvailable]);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    runCheck();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        runCheck();
        if (isPosProtectedRoute(window.location.pathname) && !hasCriticalOperationActive()) {
          applyPendingVersionIfSafe(window.location.pathname).catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    const intervalId = window.setInterval(() => {
      runCheck();
      if (isPosProtectedRoute(window.location.pathname) && !hasCriticalOperationActive()) {
        applyPendingVersionIfSafe(window.location.pathname).catch(() => {});
      }
    }, getVersionCheckIntervalMs());

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(intervalId);
    };
  }, [runCheck]);

  useEffect(() => {
    if (posRoute) {
      setUpdateModalOpen(false);
      return undefined;
    }

    if (!hasCriticalOperationActive()) {
      deferredNotifiedRef.current = false;
      const pending = readPendingVersion();
      if (pending && !updateModalOpen) {
        runCheck();
      }
      return undefined;
    }

    const pollId = window.setInterval(() => {
      if (!hasCriticalOperationActive()) {
        deferredNotifiedRef.current = false;
        runCheck();
      } else if (!deferredNotifiedRef.current) {
        deferredNotifiedRef.current = true;
        console.info('[AppVersionGuard] actualización diferida:', describeCriticalOperation());
        notifyDeferredUpdate();
      }
    }, 3000);

    return () => window.clearInterval(pollId);
  }, [pathname, posRoute, runCheck, updateModalOpen]);

  const handleUpdateNow = async () => {
    const target = pendingVersion || readPendingVersion();
    if (!target) {
      setUpdateModalOpen(false);
      return;
    }
    if (hasCriticalOperationActive()) {
      notifyDeferredUpdate();
      return;
    }
    try {
      setApplying(true);
      const result = await applyVersionUpdateNow(target);
      if (result.status === 'deferred') {
        notifyDeferredUpdate();
        setApplying(false);
        return;
      }
      window.location.reload();
    } catch (error) {
      console.warn('[AppVersionGuard] apply error:', error?.message || error);
      setApplying(false);
      toast.error('No se pudo aplicar la actualización. Intentá de nuevo.');
    }
  };

  const handlePostpone = () => {
    postponeVersionUpdate(getPostponeDurationMs());
    setUpdateModalOpen(false);
    if (toast.isActive(UPDATE_MODAL_TOAST_ID)) toast.dismiss(UPDATE_MODAL_TOAST_ID);
    toast.info('Actualización pospuesta 5 minutos.', {
      toastId: UPDATE_MODAL_TOAST_ID,
      autoClose: 4000
    });
  };

  return (
    <AppVersionUpdateModal
      isOpen={!posRoute && updateModalOpen}
      onUpdateNow={handleUpdateNow}
      onPostpone={handlePostpone}
      loading={applying}
    />
  );
};

export default AppVersionGuard;
